<script setup lang="ts">
import { ref, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useI18n } from '@/i18n'

const { t } = useI18n()

interface Scene {
  id: number
  idx: number
  start_time: number
  duration_s: number
  description: string | null
  image_prompt: string | null
  video_prompt: string | null
  status: string
  image_url: string | null
  video_url: string | null
}

const props = defineProps<{
  projectId: number
  scenes: Scene[]
  projectStatus: string
  projectMode: string
  regenerationsRemaining: number
}>()

const emit = defineEmits<{
  (e: 'validate'): void
  (e: 'regenerate', sceneId: number, newPrompt: string | null): void
  (e: 'swap', sceneAId: number, sceneBId: number): void
  (e: 'refresh'): void
}>()

const selectedScene = ref<Scene | null>(null)
const showDialog = ref(false)
const editedPrompt = ref('')
const regenerating = ref(false)

const canEdit = computed(() => 
  props.projectStatus === 'videos_ready' && props.projectMode === 'manual'
)

const canRegenerate = computed(() => props.regenerationsRemaining > 0)

// Sort scenes by idx for display
const sortedScenes = computed(() => 
  [...props.scenes].sort((a, b) => a.idx - b.idx)
)

function openSceneDialog(scene: Scene) {
  selectedScene.value = scene
  editedPrompt.value = scene.video_prompt || ''
  showDialog.value = true
}

function closeDialog() {
  showDialog.value = false
  selectedScene.value = null
  editedPrompt.value = ''
}

async function handleRegenerate() {
  if (!selectedScene.value) return
  
  if (!canRegenerate.value) {
    ElMessage.warning(t('videos.regeneration_limit_reached'))
    return
  }
  
  try {
    await ElMessageBox.confirm(
      t('videos.confirm_regenerate_message', { remaining: props.regenerationsRemaining }),
      t('videos.confirm_regenerate_title'),
      {
        confirmButtonText: t('videos.regenerate'),
        cancelButtonText: t('common.cancel'),
        type: 'warning'
      }
    )
    
    regenerating.value = true
    const newPrompt = editedPrompt.value !== selectedScene.value.video_prompt 
      ? editedPrompt.value 
      : null
    
    emit('regenerate', selectedScene.value.id, newPrompt)
    closeDialog()
    ElMessage.success(t('videos.regeneration_started'))
  } catch {
    // Cancelled
  } finally {
    regenerating.value = false
  }
}

function canMoveLeft(scene: Scene): boolean {
  return scene.idx > 0
}

function canMoveRight(scene: Scene): boolean {
  return scene.idx < props.scenes.length - 1
}

function moveScene(scene: Scene, direction: 'left' | 'right') {
  const targetIdx = direction === 'left' ? scene.idx - 1 : scene.idx + 1
  const targetScene = props.scenes.find(s => s.idx === targetIdx)
  
  if (targetScene) {
    emit('swap', scene.id, targetScene.id)
    ElMessage.success(t('videos.scenes_swapped'))
  }
}

async function validateAllVideos() {
  try {
    await ElMessageBox.confirm(
      t('videos.confirm_validate_message'),
      t('videos.confirm_validate_title'),
      {
        confirmButtonText: t('videos.validate_and_merge'),
        cancelButtonText: t('common.cancel'),
        type: 'info'
      }
    )
    emit('validate')
  } catch {
    // Cancelled
  }
}

function formatDuration(seconds: number): string {
  return `${seconds.toFixed(1)}s`
}
</script>

<template>
  <div class="video-validator">
    <!-- Header -->
    <div class="validator-header">
      <div class="header-info">
        <h2>{{ t('videos.title') }}</h2>
        <div class="header-stats">
          <span class="stat">
            <strong>{{ scenes.length }}</strong> {{ t('videos.videos') }}
          </span>
          <span class="stat regenerations" :class="{ 'warning': regenerationsRemaining <= 1 }">
            <strong>{{ regenerationsRemaining }}</strong> {{ t('videos.regenerations_left') }}
          </span>
        </div>
      </div>
      
      <div v-if="canEdit" class="header-actions">
        <el-button @click="validateAllVideos" type="success" size="large">
          {{ t('videos.validate_and_merge') }}
        </el-button>
      </div>
    </div>

    <!-- Info Alert -->
    <el-alert 
      type="info" 
      :title="t('videos.instructions')"
      show-icon
      :closable="false"
      class="instructions-alert"
    />

    <!-- Videos Grid -->
    <div class="videos-grid">
      <div 
        v-for="scene in sortedScenes" 
        :key="scene.id"
        class="video-card"
      >
        <!-- Move Arrows -->
        <div v-if="canEdit" class="move-controls">
          <button 
            class="move-btn move-left"
            :disabled="!canMoveLeft(scene)"
            @click.stop="moveScene(scene, 'left')"
            :title="t('videos.move_left')"
          >
            ◀
          </button>
          <span class="scene-order">{{ scene.idx + 1 }}</span>
          <button 
            class="move-btn move-right"
            :disabled="!canMoveRight(scene)"
            @click.stop="moveScene(scene, 'right')"
            :title="t('videos.move_right')"
          >
            ▶
          </button>
        </div>

        <!-- Video Thumbnail (using scene image as thumbnail) -->
        <div 
          class="video-container"
          :class="{ 'clickable': canEdit }"
          @click="canEdit && openSceneDialog(scene)"
        >
          <!-- Always show scene image as thumbnail -->
          <img 
            v-if="scene.image_url" 
            :src="scene.image_url" 
            :alt="`Scene ${scene.idx + 1}`"
            class="scene-image-thumb"
          />
          <div v-else class="video-placeholder">
            <span>{{ scene.idx + 1 }}</span>
          </div>
          
          <!-- Video indicator badge -->
          <div v-if="scene.video_url" class="video-badge">
            <span class="video-icon">▶</span>
          </div>
          
          <div class="video-overlay">
            <span class="edit-hint">{{ t('videos.click_to_edit') }}</span>
          </div>
          <div class="video-duration">
            {{ formatDuration(scene.duration_s) }}
          </div>
        </div>

        <div class="video-info">
          <p class="video-description">{{ scene.description || '-' }}</p>
        </div>
      </div>
    </div>

    <!-- Scene Edit Dialog -->
    <el-dialog
      v-model="showDialog"
      :title="t('videos.edit_video')"
      width="900px"
      destroy-on-close
      class="video-edit-dialog"
    >
      <div v-if="selectedScene" class="dialog-content">
        <!-- Video Preview -->
        <div class="video-preview-large">
          <video 
            v-if="selectedScene.video_url" 
            :src="selectedScene.video_url"
            controls
            class="preview-video"
          />
          <div v-else class="preview-placeholder">
            {{ t('videos.no_video') }}
          </div>
        </div>

        <!-- Scene Info -->
        <div class="scene-details">
          <div class="detail-row">
            <div class="detail-item">
              <label>{{ t('videos.scene_number') }}</label>
              <p>{{ selectedScene.idx + 1 }} / {{ scenes.length }}</p>
            </div>
            <div class="detail-item">
              <label>{{ t('videos.duration') }}</label>
              <p>{{ formatDuration(selectedScene.duration_s) }}</p>
            </div>
          </div>

          <div class="detail-item">
            <label>{{ t('script.description') }}</label>
            <p>{{ selectedScene.description || '-' }}</p>
          </div>

          <!-- Editable Video Prompt -->
          <div class="detail-item">
            <label>{{ t('script.video_prompt') }}</label>
            <el-input 
              v-model="editedPrompt" 
              type="textarea"
              :rows="4"
              :placeholder="t('script.video_prompt_placeholder')"
            />
          </div>
        </div>

        <!-- Actions -->
        <div class="dialog-actions">
          <div class="action-group">
            <el-button 
              @click="handleRegenerate" 
              type="primary"
              :disabled="!canRegenerate"
              :loading="regenerating"
              size="large"
            >
              <span class="action-icon">🔄</span>
              {{ t('videos.regenerate') }}
              <span v-if="canRegenerate" class="action-hint">
                ({{ regenerationsRemaining }} {{ t('videos.remaining') }})
              </span>
            </el-button>
            <p v-if="!canRegenerate" class="limit-warning">
              {{ t('videos.regeneration_limit_reached') }}
            </p>
          </div>
        </div>
      </div>

      <template #footer>
        <el-button @click="closeDialog">{{ t('common.close') }}</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped>
.video-validator {
  background: var(--m2v-bg-surface);
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-xl);
  padding: var(--m2v-space-6);
}

.validator-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--m2v-space-4);
  padding-bottom: var(--m2v-space-4);
  border-bottom: 1px solid var(--m2v-border-subtle);
}

.validator-header h2 {
  margin: 0 0 var(--m2v-space-2) 0;
  font-size: 1.5rem;
}

.header-stats {
  display: flex;
  gap: var(--m2v-space-4);
  color: var(--m2v-text-muted);
}

.stat strong {
  color: var(--m2v-text-primary);
}

.regenerations.warning strong {
  color: var(--el-color-warning);
}

.instructions-alert {
  margin-bottom: var(--m2v-space-6);
}

.videos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: var(--m2v-space-4);
}

.video-card {
  background: var(--m2v-bg-elevated);
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-lg);
  overflow: hidden;
  transition: all var(--m2v-transition-fast);
}

.video-card:hover {
  border-color: var(--m2v-primary);
}

/* Move Controls */
.move-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--m2v-space-3);
  padding: var(--m2v-space-2);
  background: var(--m2v-bg-base);
  border-bottom: 1px solid var(--m2v-border-subtle);
}

.move-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--m2v-primary);
  color: white;
  border: none;
  border-radius: var(--m2v-radius-md);
  cursor: pointer;
  transition: all var(--m2v-transition-fast);
  font-size: 0.875rem;
}

.move-btn:hover:not(:disabled) {
  background: var(--m2v-primary-light);
  transform: scale(1.1);
}

.move-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.scene-order {
  font-weight: 700;
  font-size: 1.25rem;
  min-width: 32px;
  text-align: center;
}

/* Video Container */
.video-container {
  position: relative;
  aspect-ratio: 16/9;
  overflow: hidden;
  background: var(--m2v-bg-base);
}

.video-container.clickable {
  cursor: pointer;
}

.video-container.clickable:hover .video-overlay {
  opacity: 1;
}

.scene-image-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Video badge indicator */
.video-badge {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 48px;
  height: 48px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--m2v-transition-fast);
  pointer-events: none;
}

.video-badge .video-icon {
  color: white;
  font-size: 1.25rem;
  margin-left: 4px; /* Slight offset for visual centering of play icon */
}

.video-container:hover .video-badge {
  background: var(--m2v-primary);
  transform: translate(-50%, -50%) scale(1.1);
}

.video-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: var(--m2v-text-muted);
}

.video-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity var(--m2v-transition-fast);
}

.edit-hint {
  color: white;
  font-weight: 600;
  padding: var(--m2v-space-2) var(--m2v-space-4);
  background: var(--m2v-primary);
  border-radius: var(--m2v-radius-md);
}

.video-duration {
  position: absolute;
  bottom: var(--m2v-space-1);
  right: var(--m2v-space-1);
  padding: 2px 8px;
  background: rgba(0, 0, 0, 0.8);
  border-radius: var(--m2v-radius-sm);
  font-size: 0.75rem;
  color: white;
}

.video-info {
  padding: var(--m2v-space-3);
}

.video-description {
  font-size: 0.75rem;
  color: var(--m2v-text-muted);
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Dialog Styles */
.dialog-content {
  display: flex;
  flex-direction: column;
  gap: var(--m2v-space-6);
}

.video-preview-large {
  width: 100%;
  border-radius: var(--m2v-radius-lg);
  overflow: hidden;
  background: black;
}

.preview-video {
  width: 100%;
  max-height: 400px;
}

.preview-placeholder {
  aspect-ratio: 16/9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--m2v-text-muted);
}

.scene-details {
  display: flex;
  flex-direction: column;
  gap: var(--m2v-space-4);
}

.detail-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--m2v-space-4);
}

.detail-item label {
  display: block;
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--m2v-text-secondary);
  margin-bottom: var(--m2v-space-2);
}

.detail-item p {
  margin: 0;
  color: var(--m2v-text-primary);
}

.dialog-actions {
  padding-top: var(--m2v-space-4);
  border-top: 1px solid var(--m2v-border-subtle);
}

.action-group {
  display: flex;
  flex-direction: column;
  gap: var(--m2v-space-2);
}

.action-icon {
  margin-right: var(--m2v-space-2);
}

.action-hint {
  font-size: 0.75rem;
  opacity: 0.8;
  margin-left: var(--m2v-space-1);
}

.limit-warning {
  margin: 0;
  font-size: 0.75rem;
  color: var(--el-color-danger);
}

@media (max-width: 768px) {
  .validator-header {
    flex-direction: column;
    gap: var(--m2v-space-4);
  }
  
  .videos-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }
  
  .detail-row {
    grid-template-columns: 1fr;
  }
}
</style>
